//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion


import { DataProvider } from '../../helpers/data-provider'
import { Dictionary } from '../../helpers/dictionary'
import { TypeHelper } from '../../helpers/type-helper'
import { ObjectCustomString } from '../../models/util/object-custom-string'

import { Enums, OpChangeState } from '../common/enums'
import { IOpObject, IReferenceType, IOpChangeState, IOpData, IAlarmConfigurable } from '../util/interfaces'
import { Utils } from '../util/utils'

import { OpDataList } from '../util/op-data-list'
import { OpData } from './op-data'
import { OpDevice } from './op-device'
import { OpLocation } from './op-location'
import { OpMeterType } from './op-meter-type'
import { OpDistributor } from './op-distributor'
import { OpAlarmDef } from './op-alarm-def'


export class OpMeter implements IReferenceType, IOpObject, IOpChangeState, IAlarmConfigurable {

    //#region Properties

    public idMeter: number
    public idMeterType: number
    public idDistributor: number

    //#endregion


    //#region Navigation Properties

    private _meterType: OpMeterType
    public get meterType(): OpMeterType {
        return this._meterType
    }

    public set meterType(value: OpMeterType) {
        this._meterType = value
        this.idMeterType = value ? value.idMeterType : null
    }

    private _distributor: OpDistributor
    public get distributor(): OpDistributor {
        return this._distributor
    }

    public set distributor(value: OpDistributor) {
        this._distributor = value
        this.idDistributor = value ? value.idDistributor : null
    }

    //#endregion


    //#region Custom Properties

    public get meterSerialNbr(): string {
        return this.dataList.getValue(Enums.DataType.METER_SERIAL_NUMBER)
    }
    public set meterSerialNbr(value: string) {
        this.dataList.setValue(value, Enums.DataType.METER_SERIAL_NUMBER)
    }

    public currentLocations: OpLocation[] = []
    public currentLocationsDict: Dictionary<OpLocation>
    public currentDevices: OpDevice[] = []
    public currentDevicesDict: Dictionary<OpDevice>

    public get currentDevice(): OpDevice {
        return _.first(this.currentDevices) || null
    }

    public get currentLocation(): OpLocation {
        return _.first(this.currentLocations) || null
    }

    public currentAlarmDefs: OpAlarmDef[]

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        this.fromJSON(opObj)
    }

    //#endregion


    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdMeter
    }

    public getReferenceKey(): number {
        return this.idMeter
    }

    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idMeter
    }

    public set idObject(value: any) {
        this.idMeter = value
    }

    public dataList: OpDataList<OpData> = new OpDataList<OpData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string


    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.meterType = opObj._MeterType ? new OpMeterType(opObj._MeterType) : null
            this.distributor = opObj._Distributor ? new OpDistributor(opObj._Distributor) : null

            this.idMeter = opObj.ID_METER
            this.idMeterType = opObj.ID_METER_TYPE
            this.idDistributor = opObj.ID_DISTRIBUTOR

            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpData(data)))
            }
            this.dynamicValues = opObj.dynamicValues
            this.opState = opObj.OpState

            if (opObj.CurrentDevices != null) {
                this.currentDevices = opObj.CurrentDevices.map((device: any) => new OpDevice(device))
            }

            if (opObj.CurrentLocations != null) {
                this.currentLocations = opObj.CurrentLocations.map((location: any) => new OpLocation(location))
            }

            if (opObj.CurrentAlarmDefs != null) {
                this.currentAlarmDefs = opObj.CurrentAlarmDefs.map((alarmDef: any) => new OpAlarmDef(alarmDef))
            }
        } else {
            this.idMeter = 0
            this.opState = OpChangeState.New
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_METER: this.idMeter,
            ID_METER_TYPE: this.idMeterType,
            ID_DISTRIBUTOR: this.idDistributor,
            CurrentLocations: this.currentLocations != null ? this.currentLocations.map((location: OpLocation) => location.toJSON()) : [],
            CurrentDevices: this.currentDevices != null ? this.currentDevices.map((device: OpDevice) => device.toJSON()) : [],
            CurrentAlarmDefs: this.currentAlarmDefs != null ? this.currentAlarmDefs.map((alarmDef: OpAlarmDef) => alarmDef.toJSON()) : [],

            CurrentLocationsDict: Utils.serializeDictionary(this.currentLocationsDict),
            CurrentDevicesDict: Utils.serializeDictionary(this.currentDevicesDict),

            _MeterType: this.meterType ? this.meterType.toJSON(compressed) : null,

            dynamicValues: this.dynamicValues,
            DataList: this.dataList.ToJSON(),
            OpState: this.opState
        }
    }

    public toString() {
        const customFormatString = ObjectCustomString.getCustomStringFormat(this)

        if (!customFormatString) {
            const meterTypeString: string = this.meterType != null ? this.meterType.toString() : TypeHelper.emptyString

            if (!Utils.isNullOrEmpty(this.meterSerialNbr)) {
                return meterTypeString + ': ' + this.meterSerialNbr
            }

            return meterTypeString + ': ' + this.idMeter
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpMeter[]): JQueryPromise<OpMeter[]> {
        return Utils
            .loadDataListNavigationProperties(dataProvider, list)
            .then(() => {
                return dataProvider.System
                    .getDictionaries([Enums.ReferenceType.IdMeterType])
                    .then((dicts: Dictionary<Dictionary<IReferenceType>>) => {
                        const meterTypeDict = dicts.tryGetValue(Enums.ReferenceType.IdMeterType);

                        _.forEach(list, meter => {
                            if (meterTypeDict != null) {
                                meter.meterType = meterTypeDict.tryGetTypedValue<OpMeterType>(meter.idMeterType);
                            }
                        });
                     
                        return list;
                    });

            });
    }

    //#endregion

    public static fromIOpObject(object: IOpObject): OpMeter {
        const newObject: OpMeter = new OpMeter(null)

        newObject.idMeter = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((d: IOpData) => OpData.fromIOpData(d)))

        return newObject
    }
}
